import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import { DataGrid, GridCellParams, GridColumns } from '@mui/x-data-grid';
import { XGridChipCell } from '@AMIEWEB/Common/XGrid/cells/XGridChipCell';
import { getColor } from '../Common/Utils';
import { formatDate } from '../../helper';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React from 'react';
import { selectOrderBonusTab } from '@AMIEWEB/Order/Store/Order.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { missingField } from 'app/constants';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { makeStyles } from 'tss-react/mui';
import i18next from 'i18next';
import { Box, Grid, Theme } from '@mui/material';

const useStyles = makeStyles()((theme: Theme) => ({
  dialog: {
    minWidth: '750px',
    padding: 0,
    '& .MuiDialogContent-root': {
      padding: 0,
      overflow: 'hidden',
    },
    '& .dialogContentRoot': {
      padding: 0,
    },
  },
  controlsGridContainer: { padding: 20 },
  header: {
    backgroundColor: theme.palette.framework.system.whisper,
    fontSize: 13,
  },
  main: {
    minHeight: '50px',
  },
  row: {
    fontSize: 14,
  },
}));

export const orderBonusChangeHistoryGridColumns = (): GridColumns => [
  {
    ...XGridDefaultColumn,
    field: 'changeDate',
    headerName: i18next.t('order.orderBonusTab.grid.changeDate'),
    flex: 0,
    width: 180,
    renderCell: params => {
      return formatDate(params?.value, 'dateAnd12HourTime');
    },
  },
  {
    ...XGridDefaultColumn,
    field: 'oldValue',
    headerName: i18next.t('order.orderBonusTab.grid.oldValue'),
    flex: 0,
    width: 120,
    renderCell: XGridChipCell({
      size: 'small',
      dynamicColor: (params: GridCellParams) => {
        return getColor(params?.value);
      },
    }),
  },
  {
    ...XGridDefaultColumn,
    field: 'newValue',
    headerName: i18next.t('order.orderBonusTab.grid.newValue'),
    flex: 0,
    width: 120,
    renderCell: XGridChipCell({
      size: 'small',
      dynamicColor: (params: GridCellParams) => {
        return getColor(params?.value);
      },
    }),
  },
  {
    ...XGridDefaultColumn,
    field: 'changedBy',
    headerName: i18next.t('order.orderBonusTab.grid.changedBy'),
    flex: 0,
    width: 160,
  },
  {
    ...XGridDefaultColumn,
    field: 'sourceSystem',
    headerName: i18next.t('order.orderBonusTab.grid.sourceSystem'),
    flex: 0,
    width: 120,
  },
];

export const OrderBonusHistory = () => {
  const [openHistoryDialog, setOpenHistoryDialog] = useState<boolean>(false);
  const formMethods = useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [rows, setRows] = useState<any>([]);
  const defaultSortColumn = { field: 'changeDate', sort: 'desc' };
  const [sortModel, setSortModel] = useState<any>([defaultSortColumn]);
  const [columns] = useState<any>(orderBonusChangeHistoryGridColumns());
  const orderBonusChangeHistory = useSelector(selectOrderBonusTab)?.changeHistoryModal;

  useEffect(() => {
    if (orderBonusChangeHistory?.open && orderBonusChangeHistory?.changeHistoryRows !== null) {
      const changeHistory = orderBonusChangeHistory?.changeHistoryRows?.map((item, index) => ({
        ...item,
        changeDate: item?.StatusChangedDate ?? missingField,
        oldValue: item?.OldStatus ?? null,
        newValue: item?.NewStatus ?? missingField,
        changedBy:
          item?.ChangedByFirstName && item?.ChangedByLastName
            ? item.ChangedByFirstName + ' ' + item.ChangedByLastName
            : missingField,
        sourceSystem: item?.SourceSystem ?? missingField,
        id: index + 1,
      }));
      setRows(changeHistory);
      setOpenHistoryDialog(orderBonusChangeHistory?.open);
    }
  }, [dispatch, orderBonusChangeHistory]);

  const DataGridToolbarTitle = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          float: 'left',
          alignItems: 'center',
          padding: 1,
          fontWeight: 400,
          fontSize: 16,
        }}
      >
        <Grid container alignItems="center" spacing={1}>
          <Grid item>{`History (${rows?.length})`}</Grid>
        </Grid>
      </Box>
    );
  };
  return (
    <div>
      <FormProvider {...formMethods}>
        <GenericDialog
          open={openHistoryDialog}
          variant="blue"
          draggable
          classes={{ paper: classes.dialog }}
          disablePortal
          disableEscapeKeyDown
          dialogTitleProps={{
            text: t(
              `Change History: ${
                orderBonusChangeHistory?.changeHistoryRows !== null &&
                orderBonusChangeHistory?.changeHistoryRows?.length > 0
                  ? orderBonusChangeHistory?.changeHistoryRows[0].BonusId
                  : ''
              }`,
            ),
            expandable: true,
            closeButton: true,
          }}
          onClose={(e, reason) => {
            if (reason !== 'backdropClick') {
              setOpenHistoryDialog(false);
              dispatch(
                orderDataActions.setOrderBonusChangeHistory({
                  open: false,
                  changeHistoryRows: null,
                }),
              );
            }
          }}
        >
          <LayoutGrid container className={classes.controlsGridContainer}>
            <LayoutGridItem item xs={12}>
              <DataGrid
                classes={{
                  main: classes.main,
                  columnHeader: classes.header,
                  row: classes.row,
                }}
                rowHeight={36}
                headerHeight={38}
                rows={rows}
                columns={columns}
                autoHeight={true}
                hideFooter={true}
                disableSelectionOnClick
                sortModel={sortModel}
                onSortModelChange={model => setSortModel(model)}
                components={{
                  Toolbar: DataGridToolbarTitle,
                }}
              />
            </LayoutGridItem>
          </LayoutGrid>
        </GenericDialog>
      </FormProvider>
    </div>
  );
};
